/**
 * GridEditPanel - Panel lateral derecho para editar mallas H3 existentes
 *
 * @version 2.0
 * @updated 2026-02-18
 * @module core/map-tools/h3-grid
 *
 * @description
 * Panel que se abre al hacer clic en el lápiz de una malla en el panel izquierdo.
 * Permite editar nombre + 4 secciones de estilo visual:
 * - ESTILO BASE: color/opacidad de relleno + color/grosor de borde
 * - HOVER: color + opacidad al pasar el mouse sobre una celda
 * - SELECCIÓN: color + opacidad al hacer clic en una celda
 * - ETIQUETAS: mostrar códigos de celda en el mapa + zoom mínimo
 *
 * Todos los cambios se aplican en tiempo real al mapa como preview.
 * Al cancelar (o cerrar sin guardar), los estilos se revierten al estado guardado en BD.
 * Al guardar, nombre + estilos se persisten en BD mediante PATCH /grids/:id.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, AlertCircle, Check } from 'lucide-react';
import { gridApi, getEffectiveStyle } from '../../spatial';
import { mapEngine } from '../../map-engine/MapEngineService';
import { useRightPanel, useUnsavedChanges } from '../../layouts/panels/RightPanel';
import type { SpatialGrid, GridStyle } from '../../spatial';
import './grid-edit-panel.css';

// ─────────────────────────────────────────
// Types
// ─────────────────────────────────────────

export interface GridEditPanelProps {
  grid: SpatialGrid;
  onClose: () => void;
  onSaved: (updated: SpatialGrid) => void;
}

// ─────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────

type MapExpression = unknown[];

/** Construye las expresiones MapLibre de fill-color y fill-opacity usando feature-state. */
function buildFillPaint(style: GridStyle): { color: MapExpression; opacity: MapExpression } {
  return {
    color: ['case',
      ['boolean', ['feature-state', 'selected'], false], style.selectedColor,
      ['boolean', ['feature-state', 'hovered'],  false], style.hoverColor,
      style.fillColor,
    ],
    opacity: ['case',
      ['boolean', ['feature-state', 'selected'], false], style.selectedOpacity,
      ['boolean', ['feature-state', 'hovered'],  false], style.hoverOpacity,
      style.fillOpacity,
    ],
  };
}

/** Aplica estilos al mapa (si las capas existen). */
function applyStylesToMap(gridId: string, style: GridStyle): void {
  const map = mapEngine.getMap();
  if (!map) return;

  const fillId  = `h3-grid-fill-${gridId}`;
  const lineId  = `h3-grid-line-${gridId}`;
  const labelId = `h3-grid-label-${gridId}`;
  const src     = `h3-grid-source-${gridId}`;

  if (map.getLayer(fillId)) {
    const { color, opacity } = buildFillPaint(style);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    map.setPaintProperty(fillId, 'fill-color',  color as any);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    map.setPaintProperty(fillId, 'fill-opacity', opacity as any);
  }
  if (map.getLayer(lineId)) {
    map.setPaintProperty(lineId, 'line-color', style.lineColor);
    map.setPaintProperty(lineId, 'line-width', style.lineWidth);
  }

  // Labels: añadir o quitar capa según showLabels
  if (style.showLabels) {
    if (!map.getLayer(labelId) && map.getSource(src)) {
      map.addLayer({
        id: labelId, type: 'symbol', source: src,
        minzoom: style.labelsMinZoom,
        layout: {
          'text-field': ['coalesce', ['get', 'cellName'], ['get', 'cellCode'], ''],
          'text-size': style.labelsSize,
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': style.fillColor,
          'text-opacity': 0.9,
          'text-halo-color': 'rgba(0,0,0,0.65)',
          'text-halo-width': 1,
        },
      });
    } else if (map.getLayer(labelId)) {
      map.setLayerZoomRange(labelId, style.labelsMinZoom, 24);
      map.setPaintProperty(labelId, 'text-color', style.fillColor);
      map.setLayoutProperty(labelId, 'text-size', style.labelsSize);
    }
  } else {
    if (map.getLayer(labelId)) map.removeLayer(labelId);
  }
}

// ─────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────

function ColorField({ id, label, value, onChange }: {
  id: string; label: string; value: string; onChange: (v: string) => void;
}): JSX.Element {
  return (
    <div className="form-group">
      <label className="form-label" htmlFor={id}>{label}</label>
      <div className="gep-color-wrapper">
        <input id={id} type="color" className="gep-color-input" value={value}
          onChange={e => onChange(e.target.value)} />
        <span className="gep-color-value">{value.toUpperCase()}</span>
      </div>
    </div>
  );
}

function OpacityField({ id, label, value, onChange, max = 1 }: {
  id: string; label: string; value: number; onChange: (v: number) => void; max?: number;
}): JSX.Element {
  return (
    <div className="form-group">
      <label className="form-label" htmlFor={id}>
        {label} <span className="gep-range-value">{Math.round(value * 100)}%</span>
      </label>
      <input id={id} type="range" className="gep-range" min={0} max={max} step={0.01}
        value={value} onChange={e => onChange(Number(e.target.value))} />
    </div>
  );
}

// ─────────────────────────────────────────
// Component
// ─────────────────────────────────────────

export function GridEditPanel({ grid, onClose, onSaved }: GridEditPanelProps): JSX.Element {
  const { registerUnsavedChangesCheck } = useRightPanel();

  // Estilos efectivos guardados (defaults aplicados para campos null)
  // MEMOIZADO para evitar recalcular en cada render
  const savedStyle = useMemo(() => getEffectiveStyle(grid), [grid]);

  // ── Form state ────────────────────────
  const [name, setName] = useState(grid.name);
  // Base
  const [fillColor,   setFillColor]   = useState(savedStyle.fillColor);
  const [fillOpacity, setFillOpacity] = useState(savedStyle.fillOpacity);
  const [lineColor,   setLineColor]   = useState(savedStyle.lineColor);
  const [lineWidth,   setLineWidth]   = useState(savedStyle.lineWidth);
  // Hover
  const [hoverColor,   setHoverColor]   = useState(savedStyle.hoverColor);
  const [hoverOpacity, setHoverOpacity] = useState(savedStyle.hoverOpacity);
  // Selección
  const [selectedColor,   setSelectedColor]   = useState(savedStyle.selectedColor);
  const [selectedOpacity, setSelectedOpacity] = useState(savedStyle.selectedOpacity);
  // Etiquetas
  const [showLabels,    setShowLabels]    = useState(savedStyle.showLabels);
  const [labelsMinZoom, setLabelsMinZoom] = useState(savedStyle.labelsMinZoom);
  const [labelsSize,    setLabelsSize]    = useState(savedStyle.labelsSize);

  // ── Operation state ───────────────────
  const [isSaving, setIsSaving] = useState(false);
  const [error,    setError]    = useState<string | null>(null);
  const [savedOk,  setSavedOk]  = useState(false);

  /**
   * CRÍTICO: Resetear todos los estados del formulario cuando cambia la malla
   * Sin esto, al cambiar de malla, los estados siguen con los valores de la malla anterior
   */
  useEffect(() => {
    console.log('[GridEditPanel] Reseteando formulario para malla:', grid.id);
    const style = getEffectiveStyle(grid);
    setName(grid.name);
    setFillColor(style.fillColor);
    setFillOpacity(style.fillOpacity);
    setLineColor(style.lineColor);
    setLineWidth(style.lineWidth);
    setHoverColor(style.hoverColor);
    setHoverOpacity(style.hoverOpacity);
    setSelectedColor(style.selectedColor);
    setSelectedOpacity(style.selectedOpacity);
    setShowLabels(style.showLabels);
    setLabelsMinZoom(style.labelsMinZoom);
    setLabelsSize(style.labelsSize);
    setError(null);
    setSavedOk(false);
  }, [grid.id, grid.name]); // Solo cuando cambia la malla

  // ── Current style (derived, MEMOIZADO) ─
  const currentStyle: GridStyle = useMemo(() => ({
    fillColor, fillOpacity, lineColor, lineWidth,
    hoverColor, hoverOpacity,
    selectedColor, selectedOpacity,
    showLabels, labelsMinZoom, labelsSize,
  }), [fillColor, fillOpacity, lineColor, lineWidth, hoverColor, hoverOpacity, selectedColor, selectedOpacity, showLabels, labelsMinZoom, labelsSize]);

  // ── Data for unsaved changes detection (MEMOIZADO) ─
  const originalData = useMemo(() => ({
    name: grid.name,
    ...savedStyle,
  }), [grid.name, savedStyle]);

  const currentData = useMemo(() => ({
    name,
    ...currentStyle,
  }), [name, currentStyle]);

  const unsavedChangesOptions = useMemo(() => ({
    confirmMessage: '¿Descartar los cambios en la malla?',
  }), []);

  // ── Unsaved changes (nombre + estilos) ─
  const { confirmDiscard } = useUnsavedChanges(
    originalData,
    currentData,
    unsavedChangesOptions,
  );

  useEffect(() => {
    registerUnsavedChangesCheck(confirmDiscard);
    return () => { registerUnsavedChangesCheck(null); };
  }, [confirmDiscard, registerUnsavedChangesCheck]);

  // ── Live preview ─────────────────────
  useEffect(() => {
    applyStylesToMap(grid.id, currentStyle);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grid.id,
      fillColor, fillOpacity, lineColor, lineWidth,
      hoverColor, hoverOpacity,
      selectedColor, selectedOpacity,
      showLabels, labelsMinZoom, labelsSize]);

  // ── Revert on unmount (close sin guardar) ─
  useEffect(() => {
    return () => { applyStylesToMap(grid.id, savedStyle); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Handlers ─────────────────────────
  const handleSave = async (): Promise<void> => {
    const trimmed = name.trim();
    if (!trimmed) { setError('El nombre no puede estar vacío'); return; }
    setIsSaving(true);
    setError(null);
    try {
      const updated = await gridApi.update(grid.id, { name: trimmed, style: currentStyle });
      setSavedOk(true);
      onSaved(updated);
      window.dispatchEvent(new CustomEvent('grid-updated', { detail: updated }));
      setTimeout(() => setSavedOk(false), 3000);
    } catch {
      setError('Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = useCallback(async (): Promise<void> => {
    if (await confirmDiscard()) onClose();
  }, [confirmDiscard, onClose]);

  // ── Render ────────────────────────────
  return (
    <div className="grid-edit-panel">

      {/* Encabezado con código/identificación del objeto */}
      <div className="tree-code-header">
        <span className="tree-code-header__label">Malla</span>
        <span className="gep-resolution-badge">R{grid.resolution}</span>
      </div>

      {/* Content */}
      <div className="grid-edit-content">

        {/* Nombre */}
        <div className="form-group">
          <label className="form-label" htmlFor="gep-name">
            Nombre <span className="required">*</span>
          </label>
          <input id="gep-name" type="text" className="form-input" value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') void handleSave(); }}
            placeholder="Nombre de la malla" maxLength={255} />
        </div>

        {/* ── ESTILO BASE ─────────────────────── */}
        <div className="gep-section-divider"><span>ESTILO BASE</span></div>
        <span className="form-hint">Los cambios se aplican en tiempo real al mapa.</span>

        <div className="gep-style-row">
          <ColorField id="gep-fill-color"     label="Color relleno"    value={fillColor}   onChange={setFillColor} />
          <OpacityField id="gep-fill-opacity" label="Opacidad relleno" value={fillOpacity} onChange={setFillOpacity} max={0.5} />
        </div>
        <div className="gep-style-row">
          <ColorField id="gep-line-color"  label="Color borde" value={lineColor} onChange={setLineColor} />
          <div className="form-group">
            <label className="form-label" htmlFor="gep-line-width">
              Grosor <span className="gep-range-value">{lineWidth}px</span>
            </label>
            <input id="gep-line-width" type="range" className="gep-range"
              min={0.5} max={4} step={0.5} value={lineWidth}
              onChange={e => setLineWidth(Number(e.target.value))} />
          </div>
        </div>

        {/* ── HOVER ───────────────────────────── */}
        <div className="gep-section-divider"><span>HOVER</span></div>

        <div className="gep-style-row">
          <ColorField id="gep-hover-color"     label="Color hover"    value={hoverColor}   onChange={setHoverColor} />
          <OpacityField id="gep-hover-opacity" label="Opacidad hover" value={hoverOpacity} onChange={setHoverOpacity} />
        </div>

        {/* ── SELECCIÓN ───────────────────────── */}
        <div className="gep-section-divider"><span>SELECCIÓN</span></div>

        <div className="gep-style-row">
          <ColorField id="gep-sel-color"     label="Color selección"    value={selectedColor}   onChange={setSelectedColor} />
          <OpacityField id="gep-sel-opacity" label="Opacidad selección" value={selectedOpacity} onChange={setSelectedOpacity} />
        </div>

        {/* ── ETIQUETAS ───────────────────────── */}
        <div className="gep-section-divider"><span>ETIQUETAS</span></div>

        <div className="form-group">
          <label className="gep-toggle-row" htmlFor="gep-show-labels">
            <input id="gep-show-labels" type="checkbox" className="gep-checkbox"
              checked={showLabels} onChange={e => setShowLabels(e.target.checked)} />
            <span className="gep-toggle-label">Mostrar códigos en el mapa</span>
          </label>
        </div>

        {showLabels && (
          <>
            <div className="form-group">
              <label className="form-label" htmlFor="gep-labels-zoom">
                Zoom mínimo <span className="gep-range-value">{labelsMinZoom}</span>
              </label>
              <input id="gep-labels-zoom" type="range" className="gep-range"
                min={6} max={18} step={1} value={labelsMinZoom}
                onChange={e => setLabelsMinZoom(Number(e.target.value))} />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="gep-labels-size">
                Tamaño texto <span className="gep-range-value">{labelsSize}px</span>
              </label>
              <input id="gep-labels-size" type="range" className="gep-range"
                min={6} max={24} step={1} value={labelsSize}
                onChange={e => setLabelsSize(Number(e.target.value))} />
            </div>
          </>
        )}

        {/* Status */}
        {error && (
          <div className="error-message">
            <AlertCircle size={14} /><span>{error}</span>
          </div>
        )}
        {savedOk && (
          <div className="success-message">
            <Check size={14} /><span>Guardado correctamente</span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="grid-edit-footer">
        <button className="cancel-btn" onClick={handleClose}>Cancelar</button>
        <button className="save-btn" onClick={() => void handleSave()}
          disabled={isSaving}>
          {isSaving
            ? <><Loader2 size={13} className="spin" /> Guardando...</>
            : 'Guardar'
          }
        </button>
      </div>

    </div>
  );
}
